import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import MuiTextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import React from 'react';

import { ManifestTables } from './ManifestTables';
import { ConditionChoice, ManifestEntry } from './PartsShipmentsHelpers';
import { StockConditionPicker } from './StockConditionPicker';
import { NSPartLocationList } from '../../../../../@types/part.type';
import {
  PartTypeActionLiveSearchItem,
  PartTypeActionLiveSearchItemCategoryCode,
} from '../../../../../@types/partType.type';

export interface ManifestSectionProps {
  manifest: ManifestEntry[];
  serialised: ManifestEntry[];
  nonSerialised: ManifestEntry[];
  partSearchInput: string;
  partOptions: PartTypeActionLiveSearchItem[];
  partSearchPending: boolean;
  conditionChoice: ConditionChoice | null;
  notice: string;
  stockResolvePending: boolean;
  onPartSearchInputChange: (val: string) => void;
  onAddItem: (item: PartTypeActionLiveSearchItem) => void;
  onChooseCondition: (row: NSPartLocationList) => void;
  onDismissConditionChoice: () => void;
  onRemoveItem: (key: string) => void;
  onUpdateQty: (key: string, qty: number) => void;
  onExport: () => void;
}

// Serialised part types can't be dispatched as a quantity — they need a
// specific serial, so their part-type rows stay visible but unselectable.
const isSerialOnlyPartType = (opt: PartTypeActionLiveSearchItem) =>
  !opt.partId && opt.categoryCode !== PartTypeActionLiveSearchItemCategoryCode.Consumable;

export const ManifestSection: React.FC<ManifestSectionProps> = ({
  manifest,
  serialised,
  nonSerialised,
  partSearchInput,
  partOptions,
  partSearchPending,
  conditionChoice,
  notice,
  stockResolvePending,
  onPartSearchInputChange,
  onAddItem,
  onChooseCondition,
  onDismissConditionChoice,
  onRemoveItem,
  onUpdateQty,
  onExport,
}) => (
  <Box sx={{ borderTop: '1px solid', borderColor: 'v4.border', mt: 1.5, pt: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
      <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>Manifest</Typography>
      {manifest.length > 0 && (
        <Button
          size="small"
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 14 }} />}
          onClick={onExport}
          sx={{ fontSize: '10px', textTransform: 'none', color: 'v4.accent' }}
        >
          Export
        </Button>
      )}
    </Box>

    <Autocomplete
      size="small"
      options={partOptions}
      getOptionLabel={(opt) => {
        const name = opt.partTypeName || opt.name || '';
        return opt.serial1 ? `${name} — ${opt.serial1}` : name;
      }}
      filterOptions={(x) => x}
      getOptionDisabled={isSerialOnlyPartType}
      inputValue={partSearchInput}
      onInputChange={(_, val, reason) => {
        if (reason !== 'reset') onPartSearchInputChange(val);
      }}
      onChange={(_, val) => {
        if (val) onAddItem(val);
      }}
      value={null}
      blurOnSelect
      clearOnBlur={false}
      loading={partSearchPending}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          placeholder="Search by serial, part type name or number..."
          sx={{ mb: 1, '& .MuiOutlinedInput-root': { fontSize: '12px' } }}
        />
      )}
      renderOption={(props, option, { index }) => {
        const optionId = option.partId ?? option.ns_partId ?? option.partTypeId;
        return (
          <li {...props} key={`${String(optionId ?? '')}-${option.serial1 ?? ''}-${index}`}>
            <Box>
              <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                {option.partTypeName || option.name || '-'}
                {option.serial1 ? ` — ${option.serial1}` : ''}
              </Typography>
              <Typography sx={{ fontSize: '10px', color: 'text.secondary' }}>
                {option.partTypeNumber || ''}
                {option.partCondition?.name ? ` · ${option.partCondition.name}` : ''}
                {option.locationName ? ` · ${option.locationName}` : ''}
                {isSerialOnlyPartType(option) ? ' · search by serial to add' : ''}
              </Typography>
            </Box>
          </li>
        );
      }}
    />

    {stockResolvePending && (
      <Typography sx={{ fontSize: '11px', color: 'text.secondary', mb: 1 }}>
        Checking stock at the source location…
      </Typography>
    )}

    {notice && (
      <Typography sx={{ fontSize: '11px', color: 'warning.main', mb: 1 }} role="status">
        {notice}
      </Typography>
    )}

    {conditionChoice && (
      <StockConditionPicker
        choice={conditionChoice}
        onChoose={onChooseCondition}
        onDismiss={onDismissConditionChoice}
      />
    )}

    <ManifestTables
      serialised={serialised}
      nonSerialised={nonSerialised}
      onRemoveItem={onRemoveItem}
      onUpdateQty={onUpdateQty}
    />

    {manifest.length === 0 && (
      <Typography sx={{ fontSize: '12px', color: 'text.secondary', textAlign: 'center', py: 1 }}>
        Search above to add parts to the manifest
      </Typography>
    )}
  </Box>
);
